// YoutubeVideoPlayer.jsx - نسخه کامل با رفع مشکل مارک واچ برای آخرین درس

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import api from "../pages/api";

const formatTime = (time) => {
  if (!time && time !== 0) return "00:00";
  if (isNaN(time)) return "00:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  const hours = Math.floor(time / 3600);
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// تبدیل ثانیه به فرمت h:m:s برای نمایش
const formatTimeDisplay = (seconds) => {
  if (!seconds && seconds !== 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    return `${hrs}:${(mins % 60).toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// تابع کمکی برای پخش ایمن ویدیو
const safePlay = (videoElement) => {
  if (!videoElement) return Promise.resolve();
  
  return videoElement.play().catch((error) => {
    if (error.name === 'AbortError' || error.message?.includes('interrupted')) {
      return Promise.resolve();
    }
    console.warn('Playback error:', error.message);
    return Promise.resolve();
  });
};

// توابع کمکی برای استایل‌های iOS
const applyIOSFullscreenStyles = (container) => {
  if (!container) return;
  
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100vw';
  container.style.height = '100vh';
  container.style.zIndex = '9999';
  container.style.backgroundColor = '#000';
  container.style.objectFit = 'contain';
  container.style.maxHeight = '100vh';
  container.style.borderRadius = '0';
  
  const video = container.querySelector('video');
  if (video) {
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.maxHeight = '100vh';
    video.style.borderRadius = '0';
  }
  
  const controls = container.querySelector('.video-controls-container');
  if (controls) {
    controls.style.zIndex = '10000';
  }
};

const resetVideoStyles = () => {
  const container = document.querySelector('.video-container');
  if (!container) return;
  
  container.style.position = '';
  container.style.top = '';
  container.style.left = '';
  container.style.width = '';
  container.style.height = '';
  container.style.zIndex = '';
  container.style.backgroundColor = '';
  container.style.objectFit = '';
  container.style.maxHeight = '';
  container.style.borderRadius = '';
  
  const video = container.querySelector('video');
  if (video) {
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.maxHeight = '80vh';
    video.style.borderRadius = '';
  }
  
  container.classList.remove('ios-fullscreen-transition');
};

const YoutubeVideoPlayer = forwardRef(({ 
  src,
  className = "",
  lessonId = null,
  onVideoEnded = null,
  autoPlay = true,
  segments = [],
  style = {},
  isLocked = false,
  onSeekAttempt = null,
  title = "ویدیو آموزشی",
  description = ""
}, ref) => {
  
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const timelineRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMiniPlayer, setIsMiniPlayer] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [wasPaused, setWasPaused] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [isChaptersOpen, setIsChaptersOpen] = useState(false);

  const hasMarkedWatched = useRef(false);
  const endedCheckInterval = useRef(null);
  const isMarkingInProgress = useRef(false); // ✅ برای جلوگیری از درخواست‌های همزمان

  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMacOS = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) && !isIOS;
  const isAppleDevice = isIOS || isMacOS;

  const videoSrc = src || "/logo.mp4";

  const chapters = segments.map((seg, index) => ({
    start: seg.start_time || 0,
    title: seg.title || `بخش ${index + 1}`,
    end: seg.end_time || 0,
    id: seg.id || index,
    order: seg.order || index + 1,
  }));

  const chapterColors = ['#FF3B30', '#FF9500', '#FFCC00', '#4CD964', '#5AC8FA', '#007AFF', '#AF52DE', '#FF2D55'];

  useImperativeHandle(ref, () => ({
    play: () => safePlay(videoRef.current),
    pause: () => videoRef.current?.pause(),
    togglePlay: togglePlayPause,
    seekTo: (time) => { 
      const video = videoRef.current;
      if (!video) return;
      
      if (isLocked && time > video.currentTime) {
        if (onSeekAttempt) onSeekAttempt('forward');
        return;
      }
      video.currentTime = time; 
    },
    getCurrentTime: () => videoRef.current?.currentTime || 0,
    getDuration: () => videoRef.current?.duration || 0,
    isPlaying: () => isPlaying,
    getChapters: () => chapters,
    enterFullscreen: toggleFullScreen,
    exitFullscreen: () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  }));

  // ========== تابع ثبت تماشای ویدیو (مارک واچ) با مدیریت بهتر ==========
  const markVideoAsWatched = useCallback(async () => {
    // اگر lessonId وجود ندارد یا قبلاً ثبت شده یا در حال ثبت است، از اجرا جلوگیری کن
    if (!lessonId || hasMarkedWatched.current || isMarkingInProgress.current) {
      console.log("⏭️ Skipping mark-watched:", { lessonId, hasMarkedWatched: hasMarkedWatched.current, isMarking: isMarkingInProgress.current });
      return;
    }

    // تنظیم پرچم در حال اجرا
    isMarkingInProgress.current = true;

    try {
      console.log(`📤 ارسال درخواست mark-watched برای درس ${lessonId}...`);
      
      const response = await api.post(`/pre_student/api/v3/lesson/${lessonId}/mark-watched/`, {});
      
      if (response.status === 200 || response.status === 201) {
        console.log("✅ ویدیو با موفقیت ثبت شد:", response.data);
        hasMarkedWatched.current = true;
        
        if (onVideoEnded && typeof onVideoEnded === 'function') {
          onVideoEnded({
            lesson_id: lessonId,
            lesson_title: response.data?.lesson_title || "این درس",
            star: response.data?.star || 0,
            remaining_star: response.data?.remaining_star || 0,
            is_preview: lessonId === null,
            success: true
          });
        }
      }
    } catch (error) {
      console.error("❌ خطا در ثبت تماشای ویدیو:", error);
      
      // اگر خطای 403 یا 401 بود، یعنی کاربر دسترسی ندارد
      if (error.response?.status === 403 || error.response?.status === 401) {
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.message || 
                        "شما به این درس دسترسی ندارید!";
        hasMarkedWatched.current = true;
        
        if (onVideoEnded && typeof onVideoEnded === 'function') {
          onVideoEnded({
            lesson_id: lessonId,
            lesson_title: "این درس",
            star: 0,
            remaining_star: 0,
            is_preview: lessonId === null,
            error: errorMsg,
            success: false
          });
        }
      } else {
        // برای خطاهای دیگر، پرچم را reset کن تا دوباره تلاش شود
        hasMarkedWatched.current = false;
        // خطاهای شبکه را لاگ کن اما درخواست دوباره ارسال می‌شود
        if (error.response?.status === 500 || error.response?.status === 502 || error.response?.status === 503) {
          console.log("🔄 خطای سرور، درخواست دوباره ارسال می‌شود...");
          // بعد از 2 ثانیه دوباره تلاش کن
          setTimeout(() => {
            isMarkingInProgress.current = false;
            markVideoAsWatched();
          }, 2000);
          return;
        }
      }
    } finally {
      isMarkingInProgress.current = false;
    }
  }, [lessonId, onVideoEnded]);

  // ========== چک کردن پایان ویدیو با دقت بیشتر ==========
  const checkIfVideoEnded = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    
    // اگر ویدیو قبلاً ثبت شده یا در حال ثبت است، بررسی نکن
    if (hasMarkedWatched.current || isMarkingInProgress.current) return;
    
    const progressPercent = (video.currentTime / video.duration) * 100;
    
    // وقتی ویدیو به 95% یا بیشتر رسید، مارک واچ کن
    if (progressPercent >= 99.8) {
      console.log(`🎯 ویدیو به ${progressPercent.toFixed(1)}% رسید، ارسال درخواست mark-watched...`);
      markVideoAsWatched();
    }
  }, [markVideoAsWatched]);

  // ========== توابع کنترلی ==========

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      safePlay(video);
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  }, []);

  const handleVolumeChange = useCallback((e) => {
    const val = parseFloat(e.target.value);
    const video = videoRef.current;
    if (!video) return;
    video.volume = val;
    video.muted = val === 0;
  }, []);

  const changePlaybackSpeed = useCallback(() => {
    let newSpeed = playbackRate + 0.25;
    if (newSpeed > 2) newSpeed = 0.5;
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = newSpeed;
    setPlaybackRate(newSpeed);
  }, [playbackRate]);

  // ========== تابع Fullscreen ==========
  const toggleFullScreen = useCallback(async () => {
    try {
      const video = videoRef.current;
      const container = videoContainerRef.current;

      const isFullscreenNow = document.fullscreenElement || 
                             video?.webkitDisplayingFullscreen || 
                             container?.webkitDisplayingFullscreen;

      if (isFullscreenNow) {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else if (video?.webkitExitFullscreen) {
          await video.webkitExitFullscreen();
        } else if (container?.webkitRequestFullscreen) {
          if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          }
        }
        
        setIsFullScreen(false);
        document.body.classList.remove('ios-fullscreen-active');
        document.body.classList.remove('fullscreen-active');
        
        if (window.screen?.orientation) {
          try {
            await window.screen.orientation.unlock();
          } catch (e) {
            console.log('باز کردن قفل چرخش صفحه ممکن نیست:', e);
          }
        }
        
        resetVideoStyles();
        return;
      }

      if (isIOS && video) {
        if (video.webkitEnterFullscreen) {
          try {
            container.classList.add('ios-fullscreen-transition');
            document.body.classList.add('ios-fullscreen-active');
            
            await video.webkitEnterFullscreen();
            setIsFullScreen(true);
            
            setTimeout(() => {
              applyIOSFullscreenStyles(container);
            }, 100);
            
            if (window.screen?.orientation) {
              try {
                await window.screen.orientation.lock('landscape');
              } catch (e) {
                console.log('چرخش صفحه در iOS ممکن نیست:', e);
              }
            }
            return;
          } catch (e) {
            console.log('webkitEnterFullscreen failed:', e);
            container.classList.remove('ios-fullscreen-transition');
            document.body.classList.remove('ios-fullscreen-active');
          }
        }
      }

      if (container?.requestFullscreen) {
        await container.requestFullscreen();
      } else if (container?.webkitRequestFullscreen) {
        await container.webkitRequestFullscreen();
      }
      
      setIsFullScreen(true);
      document.body.classList.add('fullscreen-active');
      
      if (isMobile && !isIOS && window.screen?.orientation) {
        try {
          await window.screen.orientation.lock('landscape');
        } catch (orientationError) {
          console.log('چرخش صفحه پشتیبانی نمی‌شود:', orientationError);
        }
      }
    } catch (error) {
      console.error('خطا در تغییر حالت Fullscreen:', error);
      setIsFullScreen(false);
      document.body.classList.remove('ios-fullscreen-active');
      document.body.classList.remove('fullscreen-active');
      resetVideoStyles();
    }
  }, [isMobile, isIOS]);

  const toggleMiniPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isMiniPlayer) {
      document.exitPictureInPicture();
    } else {
      video.requestPictureInPicture();
    }
  }, [isMiniPlayer]);

  const skip = useCallback((seconds) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newTime = video.currentTime + seconds;
    if (isLocked && seconds > 0 && newTime > video.currentTime) {
      if (onSeekAttempt) {
        onSeekAttempt();
      }
      return;
    }
    video.currentTime = newTime;
  }, [isLocked, onSeekAttempt]);

  const handleChapterClick = useCallback((time) => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isLocked && time > video.currentTime) {
      if (onSeekAttempt) {
        onSeekAttempt();
      }
      return;
    }
    
    video.currentTime = time;
    if (video.paused) {
      safePlay(video);
    }
    setIsChaptersOpen(false);
  }, [isLocked, onSeekAttempt]);

  // ====== تایم‌لاین ======

  const handleTimelineClick = useCallback((e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const clickTime = percent * duration;
    
    const video = videoRef.current;
    if (!video) return;
    
    if (isLocked && clickTime > video.currentTime) {
      if (onSeekAttempt) {
        onSeekAttempt();
      }
      return;
    }
    
    video.currentTime = clickTime;
    if (video.paused) {
      safePlay(video);
    }
  }, [duration, isLocked, onSeekAttempt]);

  const handleTimelineMove = useCallback((e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setPreviewPosition(percent);
    if (isScrubbing) {
      e?.preventDefault();
      const clickTime = percent * duration;
      
      const video = videoRef.current;
      if (!video) return;
      
      if (isLocked && clickTime > video.currentTime) {
        return;
      }
      
      setProgress(percent);
      video.currentTime = clickTime;
    }
  }, [isScrubbing, duration, isLocked]);

  const handleTimelineMouseDown = useCallback((e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const percent = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const clickTime = percent * duration;
    
    const video = videoRef.current;
    if (!video) return;
    
    if (isLocked && clickTime > video.currentTime) {
      if (onSeekAttempt) {
        onSeekAttempt();
      }
      return;
    }
    
    setIsScrubbing(true);
    setWasPaused(video.paused);
    
    if (!video.paused) {
      video.pause();
    }
    
    setProgress(percent);
    video.currentTime = clickTime;
  }, [duration, isLocked, onSeekAttempt]);

  // ====== رویدادهای ویدیو ======

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Reset state when video source changes
    hasMarkedWatched.current = false;
    isMarkingInProgress.current = false;
    setHasEnded(false);
    
    if (endedCheckInterval.current) {
      clearInterval(endedCheckInterval.current);
      endedCheckInterval.current = null;
    }

    const handlePlay = () => {
      setIsPlaying(true);
      videoContainerRef.current?.classList.remove('paused');
      setHasEnded(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      videoContainerRef.current?.classList.add('paused');
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const percent = video.currentTime / video.duration;
      setProgress(percent);
      
      // هر بار که زمان تغییر می‌کند، بررسی کن که آیا به انتها رسیده
      checkIfVideoEnded();
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      if (autoPlay && video.src) {
        safePlay(video);
      }
    };

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
      const volumeLevel = video.muted || video.volume === 0 ? 'muted' : video.volume < 0.5 ? 'low' : 'high';
      videoContainerRef.current?.setAttribute('data-volume-level', volumeLevel);
    };

    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    const handleWebkitFullscreenChange = () => {
      const isFullscreen = !!(document.fullscreenElement || 
                             video?.webkitDisplayingFullscreen || 
                             videoContainerRef.current?.webkitDisplayingFullscreen);
      setIsFullScreen(isFullscreen);
      
      if (!isFullscreen && isIOS) {
        resetVideoStyles();
        document.body.classList.remove('ios-fullscreen-active');
        document.body.classList.remove('fullscreen-active');
      } else if (isFullscreen && isIOS) {
        document.body.classList.add('ios-fullscreen-active');
        setTimeout(() => {
          applyIOSFullscreenStyles(videoContainerRef.current);
        }, 100);
      }
    };

    const handleEnterPiP = () => {
      setIsMiniPlayer(true);
      videoContainerRef.current?.classList.add('mini-player');
    };

    const handleLeavePiP = () => {
      setIsMiniPlayer(false);
      videoContainerRef.current?.classList.remove('mini-player');
    };

    const handleVideoClick = () => {
      if (video.paused) {
        safePlay(video);
      } else {
        video.pause();
      }
    };

    const handleEnded = () => {
      console.log("🎬 رویداد ended - ویدیو به پایان رسید");
      setHasEnded(true);
      setIsPlaying(false);
      videoContainerRef.current?.classList.add('paused');
      
      // وقتی ویدیو کاملاً تمام شد، حتماً مارک واچ را صدا بزن
      if (!hasMarkedWatched.current && !isMarkingInProgress.current) {
        markVideoAsWatched();
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('volumechange', handleVolumeChange);
    video.addEventListener('click', handleVideoClick);
    video.addEventListener('ended', handleEnded);

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleWebkitFullscreenChange);
    video.addEventListener('webkitbeginfullscreen', handleWebkitFullscreenChange);
    video.addEventListener('webkitendfullscreen', handleWebkitFullscreenChange);
    
    video.addEventListener('enterpictureinpicture', handleEnterPiP);
    video.addEventListener('leavepictureinpicture', handleLeavePiP);

    // interval برای چک کردن مداوم پیشرفت ویدیو (هر 1 ثانیه)
    endedCheckInterval.current = setInterval(() => {
      checkIfVideoEnded();
    }, 1000);

    const handleGlobalMouseUp = () => {
      if (isScrubbing) {
        setIsScrubbing(false);
        const videoEl = videoRef.current;
        if (videoEl && !wasPaused) {
          safePlay(videoEl);
        }
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('volumechange', handleVolumeChange);
      video.removeEventListener('click', handleVideoClick);
      video.removeEventListener('ended', handleEnded);
      
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleWebkitFullscreenChange);
      video.removeEventListener('webkitbeginfullscreen', handleWebkitFullscreenChange);
      video.removeEventListener('webkitendfullscreen', handleWebkitFullscreenChange);
      
      video.removeEventListener('enterpictureinpicture', handleEnterPiP);
      video.removeEventListener('leavepictureinpicture', handleLeavePiP);
      
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      
      if (endedCheckInterval.current) {
        clearInterval(endedCheckInterval.current);
        endedCheckInterval.current = null;
      }
    };
  }, [autoPlay, checkIfVideoEnded, isIOS, isScrubbing, wasPaused, markVideoAsWatched]);

  // ====== وقتی lessonId تغییر می‌کند، reset کن ======
  useEffect(() => {
    hasMarkedWatched.current = false;
    isMarkingInProgress.current = false;
    setHasEnded(false);
  }, [lessonId]);

  // ====== کیبورد ======

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tagName = document.activeElement.tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea') return;

      const key = e.key.toLowerCase();
      switch (key) {
        case ' ':
          if (tagName === 'button') return;
        case 'k':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'f':
          e.preventDefault();
          toggleFullScreen();
          break;
        case 'i':
          e.preventDefault();
          toggleMiniPlayer();
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'arrowleft':
        case 'j':
          e.preventDefault();
          skip(-5);
          break;
        case 'arrowright':
        case 'l':
          e.preventDefault();
          skip(5);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlayPause, toggleFullScreen, toggleMiniPlayer, toggleMute, skip]);

  // ====== رندر بخش‌های تایم‌لاین ======

  const renderTimelineChapters = () => {
    if (!chapters || chapters.length === 0) return null;
    
    const totalDuration = duration || 1;
    return chapters.map((chapter, index) => {
      const nextStart = index < chapters.length - 1 ? chapters[index + 1].start : totalDuration;
      const chapterDuration = nextStart - chapter.start;
      const widthPercent = (chapterDuration / totalDuration) * 100;
      const colorIndex = index % chapterColors.length;

      return (
        <div
          key={chapter.id || index}
          className="chapter-segment"
          data-color={colorIndex}
          style={{ width: `${Math.max(widthPercent, 0.5)}%`, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            handleChapterClick(chapter.start);
          }}
        >
          <div className="chapter-tooltip">
            <span className="chapter-time">{formatTimeDisplay(chapter.start)}</span>
            <span className="chapter-title-text">{chapter.title}</span>
          </div>
        </div>
      );
    });
  };

  // ====== رندر لیست بخش‌ها ======

  const renderChaptersList = () => {
    if (!chapters || chapters.length === 0) return null;
    
    const totalDuration = duration || 1;
    let activeIndex = -1;
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (currentTime >= chapters[i].start) {
        activeIndex = i;
        break;
      }
    }

    return (
      <div className={`chapters-list ${isChaptersOpen ? 'active' : ''}`}>
        <div className="chapters-list-inner">
          {chapters.map((chapter, index) => {
            const nextStart = index < chapters.length - 1 ? chapters[index + 1].start : totalDuration;
            const chapterDuration = nextStart - chapter.start;
            const isActive = index === activeIndex;

            return (
              <div
                key={chapter.id || index}
                className={`chapter-item ${isActive ? 'active' : ''}`}
                onClick={() => handleChapterClick(chapter.start)}
              >
                <span className="chapter-dot" style={{ background: chapterColors[index % chapterColors.length] }}></span>
                <span className="chapter-time-badge">{formatTimeDisplay(chapter.start)}</span>
                <span className="chapter-title-text">{chapter.title}</span>
                <span className="chapter-duration">{formatTimeDisplay(chapterDuration)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ====== رندر ======

  return (
    <div className={`video-player-wrapper ${className}`} style={style}>
      <div 
        className={`video-container paused ${isFullScreen ? 'full-screen' : ''} ${isMiniPlayer ? 'mini-player' : ''}`}
        ref={videoContainerRef}
        data-volume-level="high"
        data-is-ios={isIOS}
        data-is-apple={isAppleDevice}
        data-is-locked={isLocked}
        style={isFullScreen && isIOS ? {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 9999,
          backgroundColor: '#000',
          objectFit: 'contain',
          borderRadius: 0,
          overflow: 'hidden'
        } : {}}
      >
        <div className="video-controls-container">
          {/* تایم‌لاین */}
          <div 
            className={`timeline-container ${isLocked ? 'locked' : ''}`}
            ref={timelineRef}
            onClick={handleTimelineClick}
            onMouseMove={handleTimelineMove}
            onMouseDown={handleTimelineMouseDown}
            style={{
              '--progress-position': progress,
              '--preview-position': previewPosition,
            }}
          >
            <div className="timeline">
              <div className="chapters-container">
                {renderTimelineChapters()}
              </div>
              <div className="thumb-indicator"></div>
              {isLocked && (
                <div className="lock-overlay" title="درس بعدی قفل است! نمی‌توانید به جلو بروید">
                  <span>🔒</span>
                </div>
              )}
            </div>
          </div>

          {/* کنترل‌ها */}
          <div className="controls">
            <button className="play-pause-btn" onClick={togglePlayPause}>
              <svg className="play-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z" />
              </svg>
              <svg className="pause-icon" viewBox="0 0 24 24">
                <path fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z" />
              </svg>
            </button>

            <div className="volume-container">
              <button className="mute-btn" onClick={toggleMute}>
                <svg className="volume-high-icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M14,3.23V5.29C16.89,6.15 19,8.83 19,12C19,15.17 16.89,17.84 14,18.7V20.77C18,19.86 21,16.28 21,12C21,7.72 18,4.14 14,3.23M16.5,12C16.5,10.23 15.5,8.71 14,7.97V16C15.5,15.29 16.5,13.76 16.5,12M3,9V15H7L12,20V4L7,9H3Z" />
                </svg>
                <svg className="volume-low-icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M5,9V15H9L14,20V4L9,9M18.5,12C18.5,10.23 17.5,8.71 16,7.97V16C17.5,15.29 18.5,13.76 18.5,12Z" />
                </svg>
                <svg className="volume-muted-icon" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12,4L9.91,6.09L12,8.18M4.27,3L3,4.27L7.73,9H3V15H7L12,20V13.27L16.25,17.53C15.58,18.04 14.83,18.46 14,18.7V20.77C15.38,20.45 16.63,19.82 17.68,18.96L19.73,21L21,19.73L12,10.73M19,12C19,12.94 18.8,13.82 18.46,14.64L19.97,16.15C20.62,14.91 21,13.5 21,12C21,7.72 18,4.14 14,3.23V5.29C16.89,6.15 19,8.83 19,12M16.5,12C16.5,10.23 15.5,8.71 14,7.97V10.18L16.45,12.63C16.5,12.43 16.5,12.21 16.5,12Z" />
                </svg>
              </button>
              <input
                className="volume-slider"
                type="range"
                min="0"
                max="1"
                step="any"
                value={volume}
                onChange={handleVolumeChange}
              />
            </div>

            <div className="duration-container">
              <span className="current-time">{formatTime(currentTime)}</span>
              <span className="duration-separator"> / </span>
              <span className="total-time">{formatTime(duration)}</span>
            </div>

            <button className="speed-btn wide-btn" onClick={changePlaybackSpeed}>
              {playbackRate}x
            </button>

            {!isIOS && (
              <button className="mini-player-btn" onClick={toggleMiniPlayer}>
                <svg viewBox="0 0 24 24">
                  <path fill="currentColor" d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z" />
                </svg>
              </button>
            )}

            <button className="full-screen-btn" onClick={toggleFullScreen}>
              <svg className="open" viewBox="0 0 24 24">
                <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
              </svg>
              <svg className="close" viewBox="0 0 24 24">
                <path fill="currentColor" d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
              </svg>
            </button>
          </div>
        </div>

        {/* دکمه پلی بزرگ */}
        <button className="big-play-btn" onClick={togglePlayPause}>
          <svg viewBox="0 0 24 24">
            <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
          </svg>
        </button>

        <video 
          ref={videoRef} 
          src={videoSrc} 
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'contain',
            maxHeight: isFullScreen ? '100vh' : '80vh'
          }}
        >
          <track kind="captions" src="assets/subtitles.vtt" srclang="en" />
        </video>
      </div>

      {/* ====== بخش توضیحات و لیست بخش‌ها ====== */}
      {chapters && chapters.length > 0 && (
        <div className="video-description">
          <div className="description-header">
            {title && <span className="video-title">{title}</span>}
          </div>

          <div className="description-body">
            <button
              className={`chapters-toggle-btn ${isChaptersOpen ? 'active' : ''}`}
              onClick={() => setIsChaptersOpen(!isChaptersOpen)}
            >
              <span> بخش‌های مهم ویدیو</span>
              <svg className="arrow-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" />
              </svg>
            </button>

            {renderChaptersList()}

            {description && (
              <div className="description-text">
                <p>{description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

YoutubeVideoPlayer.displayName = 'YoutubeVideoPlayer';

export default YoutubeVideoPlayer;